"use client";

import Image from "next/image";
import styles from "./listing-card-premium.module.css";
import {
  MdBed,
  MdBathtub,
  MdSquareFoot,
  MdFavorite,
  MdFavoriteBorder,
  MdDirectionsCar,
} from "react-icons/md";
import { FaLocationDot } from "react-icons/fa6";
import { motion } from "framer-motion";
import { ApiError, Currencies, ListingCardData, TypeEnum } from "@/types/types";
import { useLocale, useTranslations } from "next-intl";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { m2ToSqft } from "@/lib/sqftToM2";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { formatPrice } from "@/lib/price-formatter";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";

// Import required Swiper modules & CSS
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import { useEffect, useRef, useState, useMemo } from "react";
import { DeviceType } from "@/hooks/use-device-type";
import { toast } from "react-toastify";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { env } from "@/lib/env";
import { useIsMobile } from "@/hooks/useIsMobile";
import { getLocalizedPath } from "@/lib/localizedRoutes/routeHelpers";
import { resolveCardSavedError } from "@/lib/resolve-listing-card-saved-state";
import { getMedia } from "@/lib/media/shared";
import { useOptimalImageSize } from "@/hooks/useOptimalImageSize";
import { IoImagesOutline } from "react-icons/io5";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;

export default function ListingCardPremium({
  data,
}: {
  data: ListingCardData;
}) {
  const { userData } = useUserAuthDataContext();
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `EUR-${userData.currency}` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const t = useTranslations("ListingCard");
  const locale = useLocale();
  const isSaved = useListingsCacheStore(
    (state) => state.meta[data.listing_id]?.is_saved,
  );
  const updateSavedState = useListingsCacheStore(
    (state) => state.updateSavedState,
  );

  const optimalImageSize = useOptimalImageSize();
  const isMobile = useIsMobile(1024);

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isInfoVisible, setIsInfoVisible] = useState(true);
  const touchStartY = useRef(0);
  const isDraggingCard = useRef(false);
  const pointerDownCoords = useRef<{ x: number; y: number } | null>(null);

  const listingUrl = getLocalizedPath(locale, "listing", [data.listing_id]);

  const { previewPhotos, totalPhotosCount } = useMemo(() => {
    const allPhotos = Object.entries(data.photos || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item);
    const total = allPhotos.length;
    const cappedPhotos = allPhotos.slice(0, 10).map((photo) => {
      const resolvedMedia = getMedia(photo, optimalImageSize);
      return {
        ...photo,
        resolvedMedia,
        displaySrc:
          resolvedMedia?.src || photo.localUrl || "/broken_image.webp",
        displayWebp: resolvedMedia?.webp,
      };
    });
    return { previewPhotos: cappedPhotos, totalPhotosCount: total };
  }, [data.photos, optimalImageSize]);

  const pauseSlideshow = () => {
    if (!isMobile && swiperInstance) swiperInstance.autoplay.stop();
  };

  const resumeSlideshow = () => {
    if (!isMobile && swiperInstance) swiperInstance.autoplay.start();
  };

  // Handle Swipe actions for Mobile (Hide/Show info)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const distance = touchStartY.current - touchEndY;
    if (distance > 40) {
      setIsInfoVisible(true); // Swipe Up
    } else if (distance < -40) {
      setIsInfoVisible(false); // Swipe Down
    }
  };

  // Click & Pointer Drag Handlers
  const handleCardPointerDown = (e: React.PointerEvent) => {
    pointerDownCoords.current = { x: e.clientX, y: e.clientY };
    isDraggingCard.current = false;
  };

  const handleCardPointerUp = (e: React.PointerEvent) => {
    if (pointerDownCoords.current) {
      const dx = Math.abs(e.clientX - pointerDownCoords.current.x);
      const dy = Math.abs(e.clientY - pointerDownCoords.current.y);
      if (dx > 10 || dy > 10) isDraggingCard.current = true;
    }
  };

  const handleSmartClick = async (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (isDraggingCard.current) {
      isDraggingCard.current = false;
      return;
    }
    // if (deviceType === DeviceType.desktop) {
    //   window.open(listingUrl, "_blank", "noopener,noreferrer");
    // } else {
    //   window.history.pushState(null, "", listingUrl);
    //   await openDialog(data.listing_id, locale, userData.sub);
    // }
  };

  const handleMouseEnter = () => {
    if (!isMobile && swiperInstance) {
      swiperInstance.autoplay.start();
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && swiperInstance) {
      swiperInstance.autoplay.stop();
      swiperInstance.slideTo(0); // Reset on leave
    }
  };

  const handleUserSwiperInteraction = () => {
    if (swiperInstance) swiperInstance.autoplay.stop();
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!userData.sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }

    const targetState = !isSaved;
    updateSavedState(data.listing_id, targetState);

    try {
      const response = await fetch(
        `${LISTINGSENDPOINTURL}/${targetState ? "addToSavedListings" : "removeFromSavedListings"}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: userData.idToken || "",
          },
          body: JSON.stringify({ listing_id: data.listing_id }),
        },
      );
      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        resolveCardSavedError({
          apiErr,
          listingId: data.listing_id,
          attemptedState: targetState,
          updateSavedState,
        });
        throw new Error(apiErr.message);
      }
    } catch (err) {
      toast("Unable to update saved listings.", { type: "error" });
    }
  };

  return (
    <motion.article transition={{ duration: 0.3, ease: "easeIn" }}>
      <a
        href={listingUrl}
        draggable={false}
        className={styles.cardWrapper}
        role="link"
        tabIndex={0}
        onPointerDown={handleCardPointerDown}
        onPointerUp={handleCardPointerUp}
        onClick={handleSmartClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleSmartClick(e);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Background Swiper Slideshow */}
        <div
          className={styles.swiperContainer}
          onTouchStart={handleUserSwiperInteraction}
          onClick={handleUserSwiperInteraction}
        >
          <Swiper
            onSwiper={setSwiperInstance}
            effect={"fade"}
            fadeEffect={{ crossFade: true }}
            speed={1200} // Slow fade effect
            loop={true}
            autoplay={{ delay: 2500, disableOnInteraction: true }}
            modules={[Pagination, Autoplay, EffectFade]}
            pagination={{
              clickable: true,
              dynamicBullets: true,
              el: `.${styles.customPagination}`,
            }}
            className={styles.swiperComponent}
            allowTouchMove={true}
            init={false}
          >
            {previewPhotos.length > 0 ? (
              previewPhotos.map((photoItem, index) => (
                <SwiperSlide
                  key={photoItem.MediaId || index}
                  className={styles.swiperSlideComponent}
                >
                  <picture style={{ display: "contents" }}>
                    {photoItem.displayWebp && (
                      <source
                        srcSet={photoItem.displayWebp}
                        type="image/webp"
                      />
                    )}
                    <Image
                      loading={index !== 0 ? "lazy" : undefined}
                      className={styles.image}
                      src={photoItem.displaySrc}
                      width={1200}
                      height={675}
                      unoptimized={true}
                      alt={`${t("propertyPhoto")} ${index + 1}`}
                      draggable={false}
                      priority={index === 0}
                    />
                  </picture>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide className={styles.swiperSlideComponent}>
                <Image
                  className={styles.image}
                  src="/broken_image.webp"
                  width={1200}
                  height={675}
                  alt="No photos available"
                  draggable={false}
                />
              </SwiperSlide>
            )}
          </Swiper>
        </div>

        {/* Dark Gradient Overlay for text readability */}
        <div
          className={`${styles.overlay} ${!isInfoVisible ? styles.overlayHidden : ""}`}
        ></div>

        {/* Absolute UI Layer overlaying the images */}
        <div className={styles.uiLayer}>
          {/* Top Row */}
          <div
            className={styles.topRow}
            onMouseEnter={pauseSlideshow}
            onMouseLeave={resumeSlideshow}
          >
            {/* Top-Left: Photo Count */}
            <div className={styles.photoCountPill}>
              <IoImagesOutline />
              <span>{totalPhotosCount}</span>
            </div>

            {/* Top-Right: Favorite Button */}
            <button
              aria-label={
                isSaved ? t("removeFromFavorites") : t("addToFavorites")
              }
              onClick={handleFavoriteClick}
              className={styles.favoriteBtn}
            >
              {isSaved ? (
                <MdFavorite className={styles.heartActive} />
              ) : (
                <MdFavoriteBorder />
              )}
            </button>
          </div>

          {/* Bottom Row (Hides on swipe down) */}
          <div
            className={`${styles.bottomRow} ${!isInfoVisible ? styles.infoHidden : ""}`}
          >
            {/* Bottom-Left: Listing Info */}
            <div
              className={styles.infoPanel}
              onMouseEnter={pauseSlideshow}
              onMouseLeave={resumeSlideshow}
            >
              <div className={styles.priceContainer}>
                {data.price_on_request ? (
                  <span className={styles.price}>{t("priceOnRequest")}</span>
                ) : (
                  <span className={styles.price}>
                    {userData.currency === "EUR" ? "€" : userData.currency}{" "}
                    {formatPrice(rate * data.price)}
                  </span>
                )}
              </div>

              <h2 className={styles.title} title={data.title}>
                {data.title}
              </h2>

              <address className={styles.location}>
                <FaLocationDot />
                <span>{data.address}</span>
              </address>

              <div className={styles.features}>
                {data.number_of_bedrooms && (
                  <span>
                    <MdBed /> {data.number_of_bedrooms} Beds
                  </span>
                )}
                {data.number_of_bathrooms && (
                  <span>
                    <MdBathtub /> {data.number_of_bathrooms} Baths
                  </span>
                )}
                {data.interior_space && data.type !== TypeEnum.LAND && (
                  <span>
                    <MdSquareFoot />{" "}
                    {userData.unit_system === "Metric"
                      ? data.interior_space
                      : m2ToSqft(data.interior_space)}{" "}
                    {userData.unit_system === "Metric" ? "sqm" : "sqft"}
                  </span>
                )}
                {/* {data.number_of_parking_spaces && (
                   <span><MdDirectionsCar /> {data.number_of_parking_spaces}</span>
                )} */}
              </div>
            </div>

            {/* Bottom-Right: Agent Details */}
            <div
              className={styles.agentInfo}
              onMouseEnter={pauseSlideshow}
              onMouseLeave={resumeSlideshow}
            >
              <div className={styles.agentText}>
                <span className={styles.agentName}>{data.creator?.name}</span>
                <span className={styles.agencyName}>Sotheby</span>
              </div>
              <Image
                className={styles.agentAvatar}
                src={data.creator?.photo_url || "/broken_image.webp"}
                width={48}
                height={48}
                alt={data.creator?.name || "Agent"}
                draggable={false}
              />
            </div>
          </div>
        </div>

        {/* Swiper Custom Pagination Container */}
        <div className={styles.customPagination}></div>
      </a>
    </motion.article>
  );
}
